import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogActions,
  DialogButton,
  DialogContent,
  ModalInstance,
} from '../../../../../../components/modals/utils';
import { useStore } from '../../../../../../store';
import { StorageProvider } from '../../../../../account/store/services/types';
import type { PulseCdnOptions } from '../../../../../pulsecdn/types';
import S3File from '../../store/S3File';
import ApiBrowser from './api-browser/ApiBrowser';
import classes from './FileBrowserDialog.module.css';
import PulseCdnBrowser from './pulsecdn-browser/PulseCdnBrowser';

const fileBrowserDialog = new ModalInstance<FileBrowserOptions, S3File[]>(
  'FileBrowserDialog',
);

export default observer(function FileBrowserDialog() {
  const { accountStore } = useStore();
  const selectedProvider =
    accountStore.account?.configurations.storages.selectedProvider;

  const [options] = fileBrowserDialog.useOptions();
  const [url, setUrl] = useState(options?.startUrl);
  const [noCloseReason, setNoCloseReason] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<S3File[]>([]);
  const actions = useHideableActions(options);
  const browserType = getBrowserType(url, selectedProvider);

  useCloseBlocker(noCloseReason);

  useEffect(() => {
    setUrl(options?.startUrl);
    setSelectedFiles([]);
  }, [options]);

  function onSelectClick() {
    fileBrowserDialog.close(selectedFiles);
  }

  function onClose() {
    if (noCloseReason) {
      toast(noCloseReason, { type: 'warning' });
    } else {
      fileBrowserDialog.close([]);
    }
  }

  if (!options || typeof url !== 'string') {
    return null;
  }

  return (
    <Dialog
      disableEscapeKeyDown={Boolean(noCloseReason)}
      fullWidth
      onClose={onClose}
      PaperProps={actions.paperProps}
      title="File Browser"
    >
      <DialogContent
        className={browserType === 'pulseCdn' ? classes.fill : undefined}
      >
        {browserType === 'api' && (
          <ApiBrowser
            setSelectedFiles={setSelectedFiles}
            setUrl={setUrl}
            url={url || ''}
          />
        )}
        {browserType === 'pulseCdn' && (
          <PulseCdnBrowser
            pulseCdnOptions={options.pulseCdnOptions}
            setNoCloseReason={setNoCloseReason}
            setSelectedFiles={setSelectedFiles}
            setUrl={setUrl}
            showActionButtons={actions.setVisible}
            url={url || ''}
          />
        )}
      </DialogContent>
      {actions.visible && (
        <DialogActions>
          <DialogButton
            disabled={selectedFiles.length === 0}
            onClick={onSelectClick}
            title={getSelectTitle(selectedFiles.length)}
          />
          <DialogButton onClick={onClose} title="Cancel" type="secondary" />
        </DialogActions>
      )}
    </Dialog>
  );
});

export function showFileBrowserDialog(
  options: FileBrowserOptions,
): Promise<S3File[]> {
  return fileBrowserDialog.show(options);
}

function useCloseBlocker(reason: string) {
  useEffect(() => {
    if (!reason) {
      return;
    }

    const onPopState = () => {
      window.history.pushState(null, '');
      toast(reason, { type: 'warning' });
    };

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    // guard entry so back button fires popstate
    window.history.pushState(null, '');

    window.addEventListener('popstate', onPopState);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [reason]);
}

/**
 * Hides dialog actions (e.g. while PulseCDN shows its own dialog) keeping the
 * dialog height, so the content fills the space freed by the actions
 */
function useHideableActions(resetKey: unknown) {
  const paperRef = useRef<HTMLDivElement>(null);
  const [frozenHeight, setFrozenHeight] = useState<number>();
  const visible = frozenHeight === undefined;

  useEffect(() => setFrozenHeight(undefined), [resetKey]);

  const setVisible = useCallback((value: boolean) => {
    setFrozenHeight(value ? undefined : paperRef.current?.offsetHeight);
  }, []);

  return {
    paperProps: { ref: paperRef, sx: { height: frozenHeight } },
    setVisible,
    visible,
  };
}

function getSelectTitle(fileCount: number): string {
  switch (fileCount) {
    case 0: {
      return 'Select files';
    }
    case 1: {
      return 'Select 1 file';
    }
    default: {
      return `Select ${fileCount} files`;
    }
  }
}

function getBrowserType(
  url?: string,
  selectedProvider?: StorageProvider,
): 'api' | 'pulseCdn' {
  if (!url) {
    return selectedProvider === 'pulsecdn.net' ? 'pulseCdn' : 'api';
  }

  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('pulsecdn.net')) {
      return 'pulseCdn';
    }

    return 'api';
  } catch {
    return 'api';
  }
}

export interface FileBrowserOptions {
  pulseCdnOptions?: PulseCdnOptions;
  startUrl?: string;
}
